import { ProductWithAddOns } from 'components/enrollForm/enrollFormTypes'
import { assignOptimisticPackageDiscounts } from './assignOptimisticPackageDiscounts'
import { formatPayloadToClientObject } from './form'
import { Order } from 'storeTypes'

type FormatClientObjectWithOptimisticDiscountsOptions = {
    fullGoalItems?: Order[]
    products?: ProductWithAddOns[]
}

type GoalItemWithDiscount = {
    uuid?: string
    price?: number | string
    discount_per_package?: number
}

/**
 * Like formatPayloadToClientObject, but stores optimistic net prices on new
 * goal rows (and discount_per_package). Preserved delivered/dropped rows from
 * modification merges are left unchanged — matched by uuid, not array index.
 * all_enrollment is the sum of every goal_item price after merge.
 */
export const formatClientObjectWithOptimisticDiscounts = (
    payload: Record<string, unknown>,
    {
        fullGoalItems = [],
        products = []
    }: FormatClientObjectWithOptimisticDiscountsOptions = {}
) => {
    const packets =
        (payload.packets as Parameters<
            typeof assignOptimisticPackageDiscounts
        >[0]) || []
    const assignments = assignOptimisticPackageDiscounts(
        packets,
        fullGoalItems,
        products
    )

    const assignmentByUuid = new Map<string, typeof assignments[number]>()
    packets.forEach((packet, index) => {
        if (!packet.uuid) {
            return
        }
        assignmentByUuid.set(packet.uuid, assignments[index])
    })

    const packetsWithNet = packets.map((packet, index) => ({
        ...packet,
        price: assignments[index].price
    }))

    const formatted = formatPayloadToClientObject({
        ...payload,
        packets: packetsWithNet
    })

    const goalItems = (
        (formatted.goal_items || []) as GoalItemWithDiscount[]
    ).map((item) => {
        const assignment = item.uuid
            ? assignmentByUuid.get(item.uuid)
            : undefined
        if (!assignment) {
            return item
        }
        return {
            ...item,
            price: assignment.price,
            discount_per_package: assignment.discount_per_package
        }
    })

    formatted.goal_items = goalItems
    formatted.all_enrollment = goalItems.reduce(
        (sum: number, item: GoalItemWithDiscount) =>
            sum + (Number(item.price) || 0),
        0
    )

    return formatted
}
